import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { confirmBillingEmail } from '@/services/apiClient';

/**
 * Landing page for the link in a billing-email verification message.
 *
 * Deliberately public and login-free: the person who confirms a finance address
 * is usually in accounts, not the admin who signed up, and asking them to make
 * an EchoHire account first is how verification never happens.
 */
export default function VerifyBillingEmailPage() {
    const [params] = useSearchParams();
    const [state, setState] = useState({ status: 'verifying', message: '', company: '', email: '' });

    const companyId = params.get('companyId');
    const token = params.get('token');

    useEffect(() => {
        if (!companyId || !token) {
            setState({
                status: 'error',
                message: 'This link is incomplete. Ask your account manager to send a new one.',
            });
            return;
        }

        let cancelled = false;
        confirmBillingEmail(companyId, token)
            .then(res => {
                if (cancelled) return;
                setState({
                    status: 'done',
                    message: '',
                    company: res.data?.companyName || '',
                    email: res.data?.billingEmail || '',
                });
            })
            .catch(err => {
                if (cancelled) return;
                setState({
                    status: 'error',
                    message: err.message || 'This link is no longer valid. Ask for a new one.',
                });
            });

        return () => { cancelled = true; };
    }, [companyId, token]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
            <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
                {state.status === 'verifying' && (
                    <>
                        <Loader2 className="mx-auto h-9 w-9 animate-spin text-primary" />
                        <h1 className="mt-5 text-lg font-semibold text-foreground">Confirming your address…</h1>
                    </>
                )}

                {state.status === 'done' && (
                    <>
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/10">
                            <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-500" />
                        </div>
                        <h1 className="mt-5 text-lg font-semibold text-foreground">Billing email confirmed</h1>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                            {state.email
                                ? <>Invoices for {state.company || 'your company'} will be sent to <strong className="text-foreground">{state.email}</strong>.</>
                                : <>Invoices for {state.company || 'your company'} will be sent to this address.</>}
                        </p>
                        <p className="mt-4 text-xs text-muted-foreground">
                            You can close this page.
                        </p>
                    </>
                )}

                {state.status === 'error' && (
                    <>
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
                            <XCircle className="h-7 w-7 text-destructive" />
                        </div>
                        <h1 className="mt-5 text-lg font-semibold text-foreground">We could not confirm this address</h1>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{state.message}</p>
                    </>
                )}
            </div>
        </div>
    );
}
